import {Injectable} from '@angular/core';
import OneSignal from 'onesignal-cordova-plugin';
import { BehaviorSubject } from 'rxjs';
import { UtilService } from './util.service';
import { StorageService } from './storage.service';
import { AlertController, Platform } from '@ionic/angular';

const MAX_NOTIFICATIONS = 100;

@Injectable({
  providedIn: 'root'
})
export class OneSignalService {
  private notificationsSubject = new BehaviorSubject<any[]>([]);
  public notifications$ = this.notificationsSubject.asObservable();
  private notification: any;
  public intialized: boolean = false;

  public setNotification(notification: any) {
    this.notification = notification;
  }

  public getNotification() {
    return this.notification;
  }

  constructor(
    private utilService: UtilService,
    private storageService: StorageService,
    private alertController: AlertController,
    private platform: Platform
  ) { }

  public async initStorage() {
    const storedNotifications = await this.storageService.get('notifications') || [];
    this.notificationsSubject.next(storedNotifications);
  }

  public async setupPush(oneSignalAppId: string): Promise<void> {
    if (!oneSignalAppId) {
      console.log('OneSignal setup skipped: No app ID provided');
      return;
    }

    if (this.intialized) {
      console.log('OneSignal already initialized');
      return;
    }

    OneSignal.setAppId(oneSignalAppId);
    this.initStorage();
    OneSignal.promptForPushNotificationsWithUserResponse();
    OneSignal.setNotificationWillShowInForegroundHandler((notificationReceivedEvent) => {
        const notification = notificationReceivedEvent.getNotification();
        this.addNotification(notification);
        // Here, you can control if the notification should be shown or processed.
        notificationReceivedEvent.complete(notification);
    });

    OneSignal.setNotificationOpenedHandler(async (openedResult) => {
        const notification = openedResult.notification;
        this.addNotification(notification);
        this.markAsRead(notification.notificationId);
        await this.presentNotificationAlert({ ...notification, read: true, receivedAt: new Date().toISOString()  });
    });
    this.intialized = true;
    console.log('OneSignal setup completed with app ID:', oneSignalAppId);
  }

  public addExternalUser(id: string): void {
    OneSignal.getDeviceState((deviceState)=>{
      if (deviceState && deviceState.hasNotificationPermission) {
        OneSignal.setExternalUserId(id, (results) => {
          })
      }
    });
  }

  public async markAsRead(notificationId: string): Promise<void> {
    const currentNotifications = this.notificationsSubject.value.map(n =>{
        if(n.notificationId === notificationId){
            n.read = true;
        }
        return n 
    });
    this.notificationsSubject.next(currentNotifications);
    await this.storageService.set('notifications', currentNotifications);
  }

  public getUnreadNotifications(): any[] {
    return this.notificationsSubject.value.filter(n => !n.read);
  }

  public async addNotification(notification: any): Promise<void> {
    let currentNotifications = this.notificationsSubject.value;
    const isAlreadyAdded = currentNotifications.some(n => n.notificationId === notification.notificationId);
    if(!isAlreadyAdded) {
        if (currentNotifications.length >= MAX_NOTIFICATIONS) {
          if(notification.scheduledTime){
            if(notification.scheduledTime.getTime() > currentNotifications[0].receivedAt.getTime()){
              currentNotifications = currentNotifications.pop();
              currentNotifications.push({ 
                title: notification.title,
                body: notification.message,
                notificationId: notification.notificationId, 
                read: false, 
                receivedAt: notification.scheduledTime.toISOString()
              });
            } 
          } else {
            currentNotifications = currentNotifications.pop(); 
            currentNotifications.push({ 
              ...notification, 
              read: false, 
              receivedAt: new Date().toISOString() 
            });
          }
        } else {
          if(notification.scheduledTime && notification.scheduledTime.getTime() <= Date.now()){
            currentNotifications.push({ 
              title: notification.title,
              body: notification.message,
              notificationId: notification.notificationId, 
              read: false, 
              receivedAt: notification.scheduledTime.toISOString()
            });
          } else if(!notification.scheduledTime) {
            currentNotifications.push({ 
                ...notification, 
                read: false, 
                receivedAt: new Date().toISOString() 
            });
          }
        }
        currentNotifications.sort((a, b) => new Date(b.receivedAt).getTime() - new Date(a.receivedAt).getTime());
        this.notificationsSubject.next(currentNotifications);
        this.storageService.set('notifications', currentNotifications);
    }
  }

  private async presentNotificationAlert(notification: any) {
    const alert = await this.alertController.create({
      header: notification.title, 
      subHeader: new Date(notification.receivedAt).toLocaleString('en-US', {
        hour: 'numeric', minute: 'numeric', hour12: true, 
        day: 'numeric', month: 'short', year: 'numeric'
      }), 
      message: notification.body, 
      buttons: ['OK']
    });
  
    await alert.present();
  }
}
